import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  Breadcrumbs,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  createRules,
  clearBillingManagementMessages,
} from "../../store/billingManagement/billingManagementSlice";

const ReviewBillingRules = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  // Get rule blocks and metadata from location state
  const { ruleBlocks, customerOrgCode, metadata } = location.state || {};

  // Redux state
  const {
    isCreatingRules,
    createRulesSuccess,
    createRulesError,
  } = useSelector((state) => state.billingManagement);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Accordion UI state (persisted in sessionStorage so it holds when navigating back)
  const REVIEW_ACCORDION_KEY = customerOrgCode ? `billing_review_accordion_${customerOrgCode}` : null;
  const loadAccordionState = () => {
    if (!REVIEW_ACCORDION_KEY) return { expandedCategories: [], expandedRules: [] };
    try {
      const raw = sessionStorage.getItem(REVIEW_ACCORDION_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          expandedCategories: Array.isArray(parsed.expandedCategories) ? parsed.expandedCategories : [],
          expandedRules: Array.isArray(parsed.expandedRules) ? parsed.expandedRules : [],
        };
      }
    } catch (e) {
      console.error("Error loading review accordion state:", e);
    }
    return { expandedCategories: [], expandedRules: [] };
  };
  const [accordionState, setAccordionState] = useState(loadAccordionState);

  // Restore accordion state when landing on review page (e.g. after navigating back from Create)
  useEffect(() => {
    if (REVIEW_ACCORDION_KEY && ruleBlocks) {
      setAccordionState(loadAccordionState());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [REVIEW_ACCORDION_KEY, ruleBlocks]);

  const saveAccordionState = (next) => {
    setAccordionState(next);
    if (REVIEW_ACCORDION_KEY) {
      try {
        sessionStorage.setItem(REVIEW_ACCORDION_KEY, JSON.stringify(next));
      } catch (e) {
        console.error("Error saving review accordion state:", e);
      }
    }
  };

  const toggleCategoryAccordion = (category) => {
    saveAccordionState({
      ...accordionState,
      expandedCategories: accordionState.expandedCategories.includes(category)
        ? accordionState.expandedCategories.filter((c) => c !== category)
        : [...accordionState.expandedCategories, category],
    });
  };

  const toggleRuleAccordion = (ruleKey) => {
    saveAccordionState({
      ...accordionState,
      expandedRules: accordionState.expandedRules.includes(ruleKey)
        ? accordionState.expandedRules.filter((k) => k !== ruleKey)
        : [...accordionState.expandedRules, ruleKey],
    });
  };

  // Redirect if no rule blocks
  useEffect(() => {
    if (!ruleBlocks || !customerOrgCode) {
      history.goBack();
    }
  }, [ruleBlocks, customerOrgCode, history]);

  // Storage key for persisting rule blocks
  const STORAGE_KEY = `billing_rule_blocks_${customerOrgCode}`;

  // Clear saved state
  const clearSavedState = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing saved state:", error);
    }
  };

  // Handle success
  useEffect(() => {
    if (createRulesSuccess) {
      setSnackbar({
        open: true,
        message: "Billing rules created successfully!",
        severity: "success",
      });
      // Clear saved state on successful creation
      clearSavedState();
      setTimeout(() => {
        history.push(`/billing-management/customer/${customerOrgCode}/rules`);
        dispatch(clearBillingManagementMessages());
      }, 1500);
    }
  }, [createRulesSuccess, history, customerOrgCode, dispatch]);

  // Handle error
  useEffect(() => {
    if (createRulesError) {
      const errorMessage =
        typeof createRulesError === "string"
          ? createRulesError
          : createRulesError?.message || "Failed to create billing rules. Please try again.";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  }, [createRulesError]);

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long" });
  };

  // Build scopes array from selected accounts, and services/regions only for ADJUSTMENT (AWS Usage - Adjustments)
  const buildScopes = (block, ruleCategory) => {
    const scopes = [];
    const accountOp = block.scopeOperatorAccounts === "EXCLUDE" ? "EXCLUDE" : "INCLUDE";
    const serviceOp = block.scopeOperatorServices === "EXCLUDE" ? "EXCLUDE" : "INCLUDE";
    const regionOp = block.scopeOperatorRegions === "EXCLUDE" ? "EXCLUDE" : "INCLUDE";

    // Add linked accounts to scopes (not linked_account_ids)
    if (!block.allAccounts && block.selectedAccounts && block.selectedAccounts.length > 0) {
      block.selectedAccounts.forEach((account) => {
        scopes.push({
          scope_type: "LINKED_ACCOUNT",
          scope_value: account,
          operator: accountOp,
        });
      });
    }

    // Service and Region scopes only for AWS Usage - Adjustments (ADJUSTMENT category)
    if (ruleCategory === "ADJUSTMENT") {
      if (block.selectedServices && block.selectedServices.length > 0) {
        block.selectedServices.forEach((service) => {
          scopes.push({
            scope_type: "SERVICE",
            scope_value: service,
            operator: serviceOp,
          });
        });
      }
      if (block.selectedRegions && block.selectedRegions.length > 0) {
        block.selectedRegions.forEach((region) => {
          scopes.push({
            scope_type: "REGION",
            scope_value: region,
            operator: regionOp,
          });
        });
      }
    }
    return scopes;
  };

  // Format date for API - expects YYYY-MM format, returns YYYY-MM-01
  const formatDateForAPI = (dateString) => {
    if (!dateString) return "";
    // If dateString is already in YYYY-MM format, append -01
    if (dateString.match(/^\d{4}-\d{2}$/)) {
      return `${dateString}-01`;
    }
    // Otherwise, parse and format
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}-01`;
  };

  // Get category label and description
  const getCategoryInfo = (category) => {
    if (category === "ADJUSTMENT") {
      return { label: "AWS Usage - Adjustments", description: "Apply percentage based markup or discounts to the AWS Usage charges" };
    }
    if (metadata?.categories) {
      const categoryData = metadata.categories.find((cat) => cat.category === category);
      if (categoryData) {
        return {
          label: categoryData.label,
          description: categoryData.description,
        };
      }
    }
    // Fallback mapping
    const categoryMap = {
      TRANSFER: { label: "Transfers", description: "Transfer a percentage of cost between entities." },
      FEE: { label: "Fees", description: "Apply a fee flag to matching usage." },
      EXCLUSION: { label: "Exclusions", description: "Exclude matching usage from billing." },
      CUSTOM: { label: "Custom charges", description: "Add a fixed custom charge." },
      ADJUSTMENT: { label: "AWS Usage - Adjustments", description: "Apply percentage based markup or discounts to the AWS Usage charges" },
    };
    return categoryMap[category] || { label: category, description: "" };
  };

  // Get type label and description
  const getTypeInfo = (type, category) => {
    if (metadata?.categories && category) {
      const categoryData = metadata.categories.find((cat) => cat.category === category);
      if (categoryData?.types) {
        const typeData = categoryData.types.find((t) => t.type === type);
        if (typeData) {
          return {
            label: typeData.label,
            description: typeData.description,
          };
        }
      }
    }
    // Fallback mapping
    const typeMap = {
      SPP: { label: "SPP", description: "Savings plans and reserved instance coverage." },
      BUNDLE_DISCOUNTS: { label: "Bundle discounts", description: "Bundle discount line items." },
      TAX: { label: "Tax", description: "Tax-related charges." },
      AWS_CREDITS: { label: "AWS credits", description: "AWS credits and credits-related items." },
      RESERVE_INSTANCE: { label: "Reserved instance", description: "Reserved instance charges." },
      SAVINGS_PLAN: { label: "Savings plan", description: "Savings plan charges." },
      AWS_SUPPORT: { label: "AWS support", description: "AWS support charges." },
      AWS_MARKETPLACE: { label: "AWS Marketplace", description: "AWS Marketplace charges." },
      FIXED_COST: { label: "Fixed cost", description: "Add a fixed amount to the bill." },
      MARGIN: { label: "Margin", description: "Increase costs by a percentage." },
      DISCOUNT: { label: "Discount", description: "Decrease costs by a percentage." },
    };
    return typeMap[type] || { label: type, description: "" };
  };

  // Handle create rules
  const handleCreateRules = () => {
    if (!ruleBlocks || ruleBlocks.length === 0) {
      setSnackbar({
        open: true,
        message: "No rules to create.",
        severity: "error",
      });
      return;
    }

    // Build rules array for API (each block can have multiple selected rule types)
    const rulesArray = [];

    ruleBlocks.forEach((block) => {
      const selectedRules = block.selectedRules || [];
      // Legacy: no selectedRules but has rule_category + rule_type or selectedTypes
      const rulesToEmit =
        selectedRules.length > 0
          ? selectedRules
          : block.selectedTypes?.length > 0
            ? block.selectedTypes.map((type) => ({
                category: block.rule_category,
                type,
                parameters: { enabled: true },
              }))
            : block.rule_category && block.rule_type
              ? [{ category: block.rule_category, type: block.rule_type, parameters: block.parameters || {} }]
              : [];

      rulesToEmit.forEach((rule) => {
        const parameters = { ...(rule.parameters || {}) };
        delete parameters._fullPartial;
        // For CUSTOM rules, include the custom description in parameters (separate from top-level description)
        if (rule.category === "CUSTOM" && block.customRuleDescription != null && String(block.customRuleDescription).trim() !== "") {
          parameters.description = block.customRuleDescription.trim();
        }
        rulesArray.push({
          name: rulesToEmit.length > 1 ? `${block.name} - ${getTypeInfo(rule.type, rule.category).label}` : block.name,
          description: block.description || undefined,
          rule_category: rule.category,
          rule_type: rule.type,
          cost_measurement: block.cost_measurement,
          status: block.status,
          frequency: block.frequency,
          start_date: block.frequency === "RECURRING" ? formatDateForAPI(block.start_date) : undefined,
          end_date: block.frequency === "RECURRING"
            ? (block.no_end_date ? null : formatDateForAPI(block.end_date))
            : undefined,
          scopes: buildScopes(block, rule.category),
          configuration: {
            parameters,
          },
        });
      });
    });

    dispatch(createRules({ customerOrgCode, rules: rulesArray }));
  };

  if (!ruleBlocks || !customerOrgCode) {
    return null;
  }

  const rulesCount = ruleBlocks.length;

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh", pb: 8, mb: "3rem" }}>
      {/* Breadcrumbs with Back Button */}
      <Box sx={{ mb: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Breadcrumbs
          separator={
            <Box
              component="span"
              sx={{
                color: "#cbd5e1",
                fontSize: "0.875rem",
                mx: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              /
            </Box>
          }
          aria-label="breadcrumb"
          sx={{
            "& .MuiBreadcrumbs-ol": {
              flexWrap: "nowrap",
            },
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push("/billing-management")}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Billing Management
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push(`/billing-management/customer/${customerOrgCode}/rules`)}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Billing Rules
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push(`/billing-management/customer/${customerOrgCode}/rules/create`)}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Create Billing {rulesCount > 1 ? "Rules" : "Rule"}
          </Link>
          <Typography
            variant="body2"
            sx={{
              color: "#3b82f6",
              fontSize: "0.875rem",
              fontWeight: 600,
              px: 0.5,
            }}
          >
            Review
          </Typography>
        </Breadcrumbs>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            // Don't clear state when going back - let user continue editing
            history.push(`/billing-management/customer/${customerOrgCode}/rules/create`);
          }}
          sx={{
            textTransform: "none",
            color: "#64748b",
            fontSize: "0.875rem",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: alpha("#3b82f6", 0.1),
              color: "#3b82f6",
            },
          }}
        >
          Back
        </Button>
      </Box>

      {/* Rules Summary Header */}
      <Card sx={{ mb: 3, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#ffffff" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b", mb: 1.5 }}>
            Review Your Billing Rules
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: "0.875rem", mb: 2, lineHeight: 1.6 }}>
            Please carefully review all the billing rules below before creating them. These rules will be applied to the customer's billing and will affect how costs are calculated, transferred, or adjusted. You can go back to make changes if needed.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#3b82f6",
                }}
              />
              <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 500 }}>
                {rulesCount} {rulesCount === 1 ? "Rule" : "Rules"} Configured
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#10b981",
                }}
              />
              <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 500 }}>
                Rules grouped by category
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Group Rules by Category */}
      {(() => {
        // Process all rules from all blocks (from selectedRules or legacy shape)
        const allRules = [];
        ruleBlocks.forEach((block, blockIndex) => {
          const selectedRules = block.selectedRules || [];
          const rulesToShow =
            selectedRules.length > 0
              ? selectedRules
              : block.selectedTypes?.length > 0
                ? block.selectedTypes.map((type) => ({ category: block.rule_category, type, parameters: {} }))
                : block.rule_category && block.rule_type
                  ? [{ category: block.rule_category, type: block.rule_type, parameters: block.parameters || {} }]
                  : [];
          rulesToShow.forEach((rule) => {
            // For CUSTOM rules, include customRuleDescription in parameters for display (and API payload builds it the same way)
            const params = rule.category === "CUSTOM" && block.customRuleDescription != null && String(block.customRuleDescription).trim() !== ""
              ? { ...(rule.parameters || {}), description: block.customRuleDescription.trim() }
              : rule.parameters || {};
            allRules.push({
              ...block,
              rule_category: rule.category,
              displayType: rule.type,
              rule_type: rule.type,
              parameters: params,
              displayName: rulesToShow.length > 1 ? `${block.name} - ${getTypeInfo(rule.type, rule.category).label}` : block.name,
              blockIndex,
            });
          });
        });

        // Group by category
        const groupedByCategory = allRules.reduce((acc, rule) => {
          const category = rule.rule_category;
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(rule);
          return acc;
        }, {});

        return Object.entries(groupedByCategory).map(([category, rules]) => (
          <Accordion
            key={category}
            expanded={accordionState.expandedCategories.includes(category)}
            onChange={() => toggleCategoryAccordion(category)}
            disableGutters
            square={false}
            sx={{
              mb: 2,
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              "&:before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: 20, color: "#64748b" }} />}
              sx={{
                backgroundColor: "#f8fafc",
                px: 2.5,
                py: 2,
                "& .MuiAccordionSummary-content": {
                  margin: 0,
                },
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "1rem", mb: 0.5 }}>
                  {getCategoryInfo(category).label}
                </Typography>
                {getCategoryInfo(category).description && (
                  <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                    {getCategoryInfo(category).description}
                  </Typography>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box sx={{ p: 2.5 }}>
                {rules.map((rule, ruleIndex) => {
                  const ruleKey = `${category}-${rule.blockIndex}-${rule.displayType || rule.rule_type || ruleIndex}`;
                  return (
                  <Accordion
                    key={ruleKey}
                    expanded={accordionState.expandedRules.includes(ruleKey)}
                    onChange={() => toggleRuleAccordion(ruleKey)}
                    disableGutters
                    square={false}
                    sx={{
                      mb: ruleIndex < rules.length - 1 ? 2 : 0,
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      "&:before": { display: "none" },
                      overflow: "hidden",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: "#64748b" }} />}
                      sx={{
                        backgroundColor: "#f9fafb",
                        px: 2,
                        py: 1.5,
                        "& .MuiAccordionSummary-content": {
                          margin: 0,
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "8px",
                            backgroundColor: alpha("#3b82f6", 0.08),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#3b82f6" }}>
                            {getTypeInfo(rule.displayType || rule.rule_type || "", rule.rule_category).label.charAt(0)}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, color: "#1e293b", mb: 0.25, fontSize: "0.95rem", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
                          >
                            {getTypeInfo(rule.displayType || rule.rule_type || "", rule.rule_category).label}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#64748b",
                              fontSize: "0.75rem",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                          >
                            {rule.name}
                          </Typography>
                        </Box>
                        <Chip
                          label={rule.frequency === "RECURRING" ? "Recurring" : "One-time"}
                          size="small"
                          sx={{
                            backgroundColor: rule.frequency === "RECURRING" ? alpha("#3b82f6", 0.1) : alpha("#10b981", 0.1),
                            color: rule.frequency === "RECURRING" ? "#3b82f6" : "#10b981",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, py: 2 }}>
                      {/* Type description and rule description */}
                      <Box
                        sx={{
                          mb: 2,
                          p: 1.75,
                          backgroundColor: "#f8fafc",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        {getTypeInfo(rule.displayType || rule.rule_type || "", rule.rule_category).description && (
                          <Typography sx={{ color: "#64748b", fontSize: "0.8125rem", lineHeight: 1.5 }}>
                            {getTypeInfo(rule.displayType || rule.rule_type || "", rule.rule_category).description}
                          </Typography>
                        )}
                        {rule.description && (
                          <Box
                            sx={{
                              mt: 1.5,
                              pt: 1.25,
                              borderTop: "1px solid #e2e8f0",
                            }}
                          >
                            <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, mb: 0.5 }}>
                              Rule Description
                            </Typography>
                            <Typography sx={{ color: "#1e293b", fontSize: "0.875rem", fontStyle: "italic", lineHeight: 1.5 }}>
                              "{rule.description}"
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Rule Name */}
                      {rule.name && (
                        <Box
                          sx={{
                            p: 2,
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                            mb: 2,
                          }}
                        >
                          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1, textTransform: "uppercase" }}>
                            Rule Name
                          </Typography>
                          <Typography sx={{ fontSize: "0.9375rem", color: "#1e293b", fontWeight: 600 }}>
                            {rule.name}
                          </Typography>
                        </Box>
                      )}

                      {/* Rule Details in Boxes */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {/* Parameters */}
                        {(() => {
                        const params = rule.parameters || {};
                        // For boolean-only categories, the parameter is always enabled=true
                        const displayParams = rule.selectedTypes && rule.selectedTypes.length > 0
                          ? { enabled: true }
                          : params;

                        if (Object.keys(displayParams).length > 0) {
                          // Get parameter descriptions from metadata
                          const getParamDescription = (key) => {
                            if (metadata?.categories) {
                              const categoryData = metadata.categories.find((cat) => cat.category === rule.rule_category);
                              if (categoryData?.types) {
                                const typeData = categoryData.types.find((t) => t.type === (rule.displayType || rule.rule_type));
                                if (typeData?.required_parameters) {
                                  const param = typeData.required_parameters.find((p) => p.key === key);
                                  return param?.description || "";
                                }
                              }
                            }
                            return "";
                          };

                          return (
                            <Box
                              sx={{
                                p: 2.5,
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                backgroundColor: "#ffffff",
                              }}
                            >
                              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                                Configuration Parameters
                              </Typography>
                              <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 2, lineHeight: 1.5 }}>
                                These parameters define how this rule will be applied to the billing calculations.
                              </Typography>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {Object.entries(displayParams).map(([key, value]) => {
                                  const paramDesc = getParamDescription(key);
                                  const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
                                  return (
                                    <Box
                                      key={key}
                                      sx={{
                                        p: 1.5,
                                        backgroundColor: "#f8fafc",
                                        borderRadius: "6px",
                                        border: "1px solid #e2e8f0",
                                      }}
                                    >
                                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: paramDesc ? 0.5 : 0 }}>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600, mb: 0.5 }}>
                                            {label}
                                          </Typography>
                                          {paramDesc && (
                                            <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", lineHeight: 1.4 }}>
                                              {paramDesc}
                                            </Typography>
                                          )}
                                        </Box>
                                        <Typography
                                          sx={{
                                            fontSize: "0.9375rem",
                                            color: "#3b82f6",
                                            fontWeight: 600,
                                            ml: 2,
                                          }}
                                        >
                                          {typeof value === "boolean" ? (value ? "Enabled" : "Disabled") : String(value)}
                                          {key === "percent" && "%"}
                                          {key === "amount" && "$"}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          );
                        }
                        return null;
                      })()}

                        {/* Frequency */}
                        <Box
                          sx={{
                            p: 2.5,
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                          }}
                        >
                          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                            Application Frequency
                          </Typography>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 2, lineHeight: 1.5 }}>
                            This determines how often the rule will be applied to the billing.
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Box
                              sx={{
                                p: 1.5,
                                backgroundColor: "#f8fafc",
                                borderRadius: "6px",
                                border: "1px solid #e2e8f0",
                              }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Box>
                                  <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600, mb: 0.5 }}>
                                    Frequency Type
                                  </Typography>
                                  <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>
                                    {rule.frequency === "RECURRING"
                                      ? "This rule will be applied continuously on each billing cycle."
                                      : "This rule will be applied immediately and remain in effect."}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={rule.frequency === "RECURRING" ? "Recurring" : "One-time"}
                                  size="small"
                                  sx={{
                                    backgroundColor: rule.frequency === "RECURRING" ? alpha("#3b82f6", 0.1) : alpha("#10b981", 0.1),
                                    color: rule.frequency === "RECURRING" ? "#3b82f6" : "#10b981",
                                    fontWeight: 600,
                                    fontSize: "0.8125rem",
                                  }}
                                />
                              </Box>
                            </Box>
                            {rule.frequency === "RECURRING" && (
                              <Box
                                sx={{
                                  p: 1.5,
                                  backgroundColor: "#f8fafc",
                                  borderRadius: "6px",
                                  border: "1px solid #e2e8f0",
                                }}
                              >
                                <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600, mb: 0.5 }}>
                                  Active Period
                                </Typography>
                                <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 1 }}>
                                  The rule will be active during this time period.
                                </Typography>
                                <Typography sx={{ fontSize: "0.9375rem", color: "#1e293b", fontWeight: 500 }}>
                                  {formatDateForDisplay(rule.start_date)} → {formatDateForDisplay(rule.end_date)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Scope Filters (Services, Regions, and Accounts) */}
                        {(() => {
                        const hasServices = rule.selectedServices && rule.selectedServices.length > 0;
                        const hasRegions = rule.selectedRegions && rule.selectedRegions.length > 0;
                        const hasAccountFilter = !rule.allAccounts && rule.selectedAccounts && rule.selectedAccounts.length > 0;
                        const isAllAccounts = rule.allAccounts !== false; // Default to true if undefined (for backward compatibility)
                        // Has filters if: specific accounts selected, OR services selected, OR regions selected
                        const hasAnyFilter = hasAccountFilter || hasServices || hasRegions;

                        if (hasAnyFilter) {
                          return (
                            <Box
                              sx={{
                                p: 2.5,
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                backgroundColor: "#ffffff",
                              }}
                            >
                              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                                Scope Filters
                              </Typography>
                              <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 2, lineHeight: 1.5 }}>
                                This rule will only apply to the selected accounts, services, and/or regions.
                              </Typography>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {/* Linked Accounts */}
                                {isAllAccounts && (hasServices || hasRegions) && (
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      backgroundColor: "#f8fafc",
                                      borderRadius: "6px",
                                      border: "1px solid #e2e8f0",
                                    }}
                                  >
                                    <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600, mb: 1 }}>
                                      Linked Accounts
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 1 }}>
                                      This rule applies to all accounts associated with this customer.
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                      <Chip
                                        label="All Linked Accounts"
                                        size="small"
                                        sx={{
                                          backgroundColor: alpha("#10b981", 0.1),
                                          color: "#10b981",
                                          fontWeight: 600,
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                )}
                                {hasAccountFilter && (
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      backgroundColor: "#f8fafc",
                                      borderRadius: "6px",
                                      border: "1px solid #e2e8f0",
                                    }}
                                  >
                                    <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600, mb: 1 }}>
                                      Linked Accounts ({rule.selectedAccounts.length}) — {(rule.scopeOperatorAccounts || "INCLUDE") === "EXCLUDE" ? "Exclude" : "Include"}
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 1 }}>
                                      {(rule.scopeOperatorAccounts || "INCLUDE") === "EXCLUDE"
                                        ? "This rule will exclude charges from these AWS accounts."
                                        : "This rule applies only to charges from these AWS accounts."}
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                      {rule.selectedAccounts.map((account) => (
                                        <Chip
                                          key={account}
                                          label={account}
                                          size="small"
                                          sx={{
                                            backgroundColor: "#ffffff",
                                            color: "#475569",
                                            border: "1px solid #e2e8f0",
                                            fontWeight: 500,
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                                {hasServices && (
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      backgroundColor: "#f8fafc",
                                      borderRadius: "6px",
                                      border: "1px solid #e2e8f0",
                                    }}
                                  >
                                    <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600, mb: 1 }}>
                                      AWS Services ({rule.selectedServices.length}) — {(rule.scopeOperatorServices || "INCLUDE") === "EXCLUDE" ? "Exclude" : "Include"}
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 1 }}>
                                      {(rule.scopeOperatorServices || "INCLUDE") === "EXCLUDE"
                                        ? "This rule will exclude charges from these AWS services."
                                        : "This rule applies only to charges from these AWS services."}
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                      {rule.selectedServices.map((service) => (
                                        <Chip
                                          key={service}
                                          label={service}
                                          size="small"
                                          sx={{
                                            backgroundColor: "#ffffff",
                                            color: "#475569",
                                            border: "1px solid #e2e8f0",
                                            fontWeight: 500,
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                                {hasRegions && (
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      backgroundColor: "#f8fafc",
                                      borderRadius: "6px",
                                      border: "1px solid #e2e8f0",
                                    }}
                                  >
                                    <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600, mb: 1 }}>
                                      AWS Regions ({rule.selectedRegions.length}) — {(rule.scopeOperatorRegions || "INCLUDE") === "EXCLUDE" ? "Exclude" : "Include"}
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 1 }}>
                                      {(rule.scopeOperatorRegions || "INCLUDE") === "EXCLUDE"
                                        ? "This rule will exclude charges from these AWS regions."
                                        : "This rule applies only to charges from these AWS regions."}
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                      {rule.selectedRegions.map((region) => (
                                        <Chip
                                          key={region}
                                          label={region}
                                          size="small"
                                          sx={{
                                            backgroundColor: "#ffffff",
                                            color: "#475569",
                                            border: "1px solid #e2e8f0",
                                            fontWeight: 500,
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        } else {
                          // No filters at all - ALL accounts, no services, no regions
                          return (
                            <Box
                              sx={{
                                p: 2.5,
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                backgroundColor: "#f8fafc",
                              }}
                            >
                              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1, textTransform: "uppercase" }}>
                                Scope Filters
                              </Typography>
                              <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
                                No filters applied. This rule will apply to all accounts, services, and regions.
                              </Typography>
                            </Box>
                          );
                        }
                      })()}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        ));
      })()}

      {/* Important Information */}
      <Card sx={{ mb: 3, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#fff7ed" }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: "#f59e0b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                mt: 0.25,
              }}
            >
              <Typography sx={{ fontSize: "0.875rem", color: "#ffffff", fontWeight: 600 }}>!</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b", mb: 1 }}>
                What happens when you create these rules?
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#64748b", fontSize: "0.875rem", lineHeight: 1.8 }}>
                <li>These billing rules will be immediately applied to the customer's billing configuration.</li>
                <li>The rules will affect how costs are calculated, transferred, or adjusted in future billing cycles.</li>
                <li>For recurring rules, they will be active from the start date you specified.</li>
                <li>You can view, edit, or delete these rules later from the Billing Rules page.</li>
                <li>Changes may take effect in the next billing cycle depending on the rule frequency.</li>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 4 }}>
        <Button
          onClick={() => history.goBack()}
          disabled={isCreatingRules}
          sx={{
            textTransform: "none",
            color: "#475569",
          }}
        >
          Back
        </Button>
        <Button
          onClick={handleCreateRules}
          disabled={isCreatingRules}
          variant="contained"
          sx={{
            textTransform: "none",
            backgroundColor: "#3b82f6",
            "&:hover": {
              backgroundColor: "#2563eb",
            },
            "&:disabled": {
              backgroundColor: "#cbd5e1",
            },
          }}
        >
          {isCreatingRules ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "#ffffff" }} />
              Creating...
            </Box>
          ) : (
            "Create billing rules"
          )}
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewBillingRules;
